import { AxiosInstance } from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';

import type { Nullable, DTO } from '@/type-utils';

import WebStorageService from '@/services/client/WebStorageService';
import siteCached from '@/services/utils/siteCached';
import { exhaustiveGuard } from '@/utils/function-utils';
import type { IReturnOrder } from '@/services/models/Returns';
import ServerReturnsLabelService from '@/services/serverless/ServerReturnsLabelService';
import { RequestError } from '@/utils/errors';
import { clientOnlyMethod } from '../../utils/environment-utils';

import Service from '../../Service';

import type {
  IReturnsFlowState,
  ReturnsFlowStateModel
} from '../../models/Returns/flow';

import { EnvironmentService } from '../EnvironmentService';
import ReturnsLabelServiceMock from './ReturnsLabelServiceMock';
import { IReturnLabel } from './contracts/IReturnLabel';
import type { IReturnLabelParams } from './contracts/IReturnLabelParams';
import { LabelService } from './contracts/LabelService';
import type { IReprintLabel } from './contracts/IReprintLabel';
import ConfigurationService, { type Config } from '../ConfigurationService';

/** The session storage key for the returns flow state. */
export const FLOW_STATE_SESSION_KEY = 'flowState';

/** Abstracts returns for the project. */
export class ReturnsLabelService extends Service {
  /**
   * Gets either the current client or creates a new one.
   * @returns An Axios instance.
   */
  @siteCached
  private get client(): AxiosInstance {
    return createAPIClient({ baseURL: '/api/returns' });
  }

  /**
   * Gets the existing client or creates a new one
   * if none exists.
   * @returns A `Config<'returns'>`.
   */
  @siteCached
  private get returnsConfig(): Config<'returns'> {
    const config = ConfigurationService.getConfig('returns');
    return config;
  }

  /**
   * The stub method to get the ReturnsLabel. Here it uses mock data in order to
   * assemble the request body, but in the future it should use the return form
   * to supply this address data and order number.
   *
   * @param order - The return order containing order number, items and metadata.
   * @param isQRCode - Is this label a qr code or a returns label.
   * @returns The label response, when the UI is in place we should trim
   * this down to what is needed for the frontend.
   * @throws If run on the server.
   */
  public async getLabel(
    order: Omit<IReturnOrder, 'orderID' | 'returnShippingMethod'>,
    isQRCode: boolean
  ): Promise<IReturnLabel> {
    try {
      const labelService = this.getLabelService();

      if ((typeof window === "undefined")) {
        return await ServerReturnsLabelService.getLabel({
          order,
          isQRCode,
          labelService
        });
      }

      const body: IReturnLabelParams = { order, isQRCode, labelService };

      const res = await this.client.post<IReturnLabel>('label', body);
      return res.data;
    } catch (error) {
      throw new RequestError('Failed to create a return label.', {
        cause: error
      });
    }
  }

  /**
   * Takes in shipmentID from the frontend and uses it to request a reprint label.
   *
   * @param shipmentID - The identifier for this shipment.
   * @param transactionID - The identifier for this return.
   * @param isQRCode - Is this label a qr code or a returns label.
   * @param labelService - The label service to use.
   * @returns A reprint label.
   */
  public async reprintLabel(
    shipmentID: string,
    transactionID: string,
    isQRCode: boolean,
    labelService: LabelService
  ): Promise<IReprintLabel> {
    if ((typeof window === "undefined")) {
      return ServerReturnsLabelService.reprintLabel(
        shipmentID,
        transactionID,
        isQRCode,
        labelService
      );
    }

    const res = await this.client.post<IReprintLabel>(`label/reprint`, {
      shipmentID,
      transactionID,
      isQRCode,
      labelService
    });

    return res.data;
  }

  /**
   * Saves the provided {@link IReturnsFlowState returns flow state} to Session.
   *
   * @param flowState - The flow state to save.
   */
  @clientOnlyMethod public saveFlowStateToSessionStorage(
    flowState: ReturnsFlowStateModel
  ): void {
    const dto = flowState.toDTO();
    WebStorageService.sessionStorage?.setItem(FLOW_STATE_SESSION_KEY, dto);
  }

  /**
   * Construct a {@link ReturnsFlowStateModel} from the returns flow data
   * stored in the current session storage (if any).
   *
   * @returns A {@link ReturnsFlowStateModel}, or `null` if no returns flow data is found in session storage.
   */
  @clientOnlyMethod
  public getFlowStateFromSessionStorage(): Nullable<DTO<IReturnsFlowState>> {
    const dto = WebStorageService.sessionStorage?.getItem<
      DTO<IReturnsFlowState>
    >(FLOW_STATE_SESSION_KEY);

    return dto;
  }

  /**
   * Gets the label service for the current brand and locale.
   * @returns The label service.
   * @throws If the label service is unknown.
   */
  public getLabelService(): LabelService {
    const labelService = this.returnsConfig.getSetting('labelServices')
      .value as LabelService;
    switch (labelService) {
      case LabelService.UPS:
        return LabelService.UPS;
      case LabelService.PitneyBowes:
        return LabelService.PitneyBowes;
      default:
        return exhaustiveGuard(
          labelService,
          `Unknown label service: ${labelService}.`
        );
    }
  }
}

export default ReturnsLabelService.withMock(
  new ReturnsLabelServiceMock(ReturnsLabelService)
);
